// Mock EMR data: networks, providers, families, members, fee schedules, UM rules, authorizations, diagnoses, and medical necessity criteria.
// It lives in the browser until the DUMMY_EMR table exists. Keep the shape table-like so it can move as is.

const KEY = "internal-emr-v5";

export const PROVIDER_TYPES = [
    { value: "professional", label: "Professional", description: "A doctor or clinic that bills on a CMS-1500." },
    { value: "institutional", label: "Institutional", description: "A hospital or facility that bills on a UB-04." },
];

export const NETWORK_KINDS = [
    { value: "network", label: "Network", description: "A contracted network a plan can use." },
    { value: "reference", label: "Out-of-network pricing", description: "Prices used when the provider is out of network." },
];

export const UM_SOURCES = [
    { value: "contract", label: "Provider contract", description: "The provider takes the penalty and can't bill the member for it." },
    { value: "plan", label: "Benefit plan", description: "The member takes the penalty." },
];

export const UM_WHEN = [
    { value: "always", label: "Always" },
    { value: "notEmergency", label: "Only when it is not an emergency" },
];

export const UM_MISSING = [
    { value: "penalty", label: "Charge a penalty" },
    { value: "deny", label: "Deny the line" },
    { value: "pend", label: "Hold the line for review" },
];

export const AUTH_TYPES = [
    { value: "authorization", label: "Authorization" },
    { value: "referral", label: "Referral" },
];

export const AUTH_STATUSES = [
    { value: "approved", label: "Approved" },
    { value: "denied", label: "Denied" },
    { value: "pended", label: "Pending" },
];

export const RELATIONSHIPS = [
    { value: "subscriber", label: "Subscriber", description: "The employee who holds the coverage." },
    { value: "spouse", label: "Spouse" },
    { value: "domesticPartner", label: "Domestic partner" },
    { value: "child", label: "Child", description: "Covered through the end of the month they turn 26." },
];

export const SEXES = [
    { value: "F", label: "Female" },
    { value: "M", label: "Male" },
    { value: "U", label: "Unknown" },
];

export const DEPENDENT_AGE_LIMIT = 26;

export const NECESSITY_MODES = [
    { value: "any", label: "At least one" },
    { value: "all", label: "All of them" },
];

export const NECESSITY_NOT_MET = [
    { value: "pend", label: "Send to a nurse or doctor for review" },
    { value: "deny", label: "Deny the request" },
];

// A small sample of ICD-10-CM codes. The full list has about 70,000.
export const ICD_CODES = [
    ["Z00.00", "Adult checkup, no abnormal findings"],
    ["Z00.01", "Adult checkup, with abnormal findings"],
    ["Z12.31", "Screening mammogram"],
    ["Z12.11", "Screening for colon cancer"],
    ["Z34.00", "Normal pregnancy, first visit"],
    ["E11.9", "Type 2 diabetes"],
    ["E66.01", "Severe obesity from excess calories"],
    ["Z68.41", "BMI 40.0 to 44.9, adult"],
    ["I10", "High blood pressure"],
    ["I20.9", "Angina, chest pain from the heart"],
    ["I21.4", "Heart attack, non-ST elevation"],
    ["I25.10", "Coronary artery disease"],
    ["R07.9", "Chest pain"],
    ["J18.9", "Pneumonia"],
    ["N39.0", "Urinary tract infection"],
    ["R10.9", "Belly pain"],
    ["K35.80", "Appendicitis"],
    ["M54.50", "Low back pain"],
    ["M54.16", "Pinched nerve in the low back"],
    ["M51.26", "Slipped disc, low back"],
    ["M62.81", "Muscle weakness"],
    ["M17.11", "Arthritis, right knee"],
    ["S83.241A", "Torn meniscus, right knee, first visit"],
    ["M23.221", "Meniscus damage, right knee"],
    ["Z47.1", "Aftercare after joint replacement"],
    ["S13.4XXA", "Neck sprain, first visit"],
    ["G43.909", "Migraine"],
    ["R51.9", "Headache"],
    ["S06.0X1A", "Concussion with brief loss of consciousness"],
    ["R29.818", "New nerve or brain symptoms"],
    ["C50.911", "Breast cancer, right"],
    ["F32.9", "Depression"],
    ["Z74.01", "Bed-confined"],
    ["R06.02", "Short of breath"],
].map(([code, description]) => ({ code, description }));

const rates = (factor, list) => list.map(([code, amount, modifier]) => ({
    code,
    ...(modifier ? { modifier } : {}),
    amount: Math.round(amount * factor * 100) / 100,
}));

const BASE_RATES = [
    ["99202", 75],
    ["99203", 115],
    ["99204", 170],
    ["99212", 60],
    ["99213", 95],
    ["99214", 135],
    ["99215", 185],
    ["99244", 210],
    ["99385", 150],
    ["99395", 140],
    ["99396", 150],
    ["99283", 120],
    ["99284", 210],
    ["99285", 310],
    ["36415", 4],
    ["80053", 14],
    ["85025", 9],
    ["80061", 16],
    ["71046", 38],
    ["71046", 12, "26"],
    ["72148", 360],
    ["74177", 420],
    ["97110", 32],
    ["97161", 90],
    ["98941", 42],
    ["97810", 38],
    ["97811", 28],
    ["45378", 520],
    ["77067", 140],
    ["29881", 1150],
    ["A0427", 650],
    ["A0428", 380],
    ["A0429", 480],
    ["A0425", 11],
    ["A0431", 9800],
    ["A0436", 95],
    ["0450", 1400],
    ["0120", 2600],
    ["0360", 3800],
    ["0300", 45],
    ["0320", 180],
];

const seed = () => ({
    networks: [
        { id: "net-centivo", name: "Centivo partnership", kind: "network" },
        { id: "net-cigna", name: "Cigna", kind: "network" },
        { id: "net-oon", name: "Out-of-network pricing", kind: "reference" },
    ],
    providers: [
        { id: "prv-patel", name: "Dr. Maya Patel", npi: "1811111111", type: "professional", taxonomy: "207Q00000X", specialty: "Family medicine", networkIds: ["net-centivo"] },
        { id: "prv-okafor", name: "Dr. Sara Okafor", npi: "1812121212", type: "professional", taxonomy: "208000000X", specialty: "Pediatrics", networkIds: ["net-centivo", "net-cigna"] },
        { id: "prv-wright", name: "Dr. James Wright", npi: "1813131313", type: "professional", taxonomy: "207R00000X", specialty: "Internal medicine", networkIds: ["net-cigna"] },
        { id: "prv-chen", name: "Dr. Leo Chen", npi: "1822222222", type: "professional", taxonomy: "207RC0000X", specialty: "Cardiology", networkIds: ["net-centivo", "net-cigna"] },
        { id: "prv-ruiz", name: "Dr. Ana Ruiz", npi: "1833333333", type: "professional", taxonomy: "207V00000X", specialty: "OB/GYN", networkIds: ["net-cigna"] },
        { id: "prv-sunrise", name: "Sunrise Chiropractic", npi: "1844444444", type: "professional", taxonomy: "111N00000X", specialty: "Chiropractor", networkIds: [] },
        { id: "prv-peak", name: "Peak Physical Therapy", npi: "1899999990", type: "professional", taxonomy: "225100000X", specialty: "Physical therapist", networkIds: ["net-cigna", "net-centivo"] },
        { id: "prv-quicklab", name: "QuickLab", npi: "1855555555", type: "professional", taxonomy: "291U00000X", specialty: "Clinical lab", networkIds: ["net-centivo", "net-cigna"] },
        { id: "prv-clearview", name: "Clearview Imaging", npi: "1899999991", type: "professional", taxonomy: "2085R0202X", specialty: "Diagnostic radiology", networkIds: ["net-cigna", "net-centivo"] },
        { id: "prv-metro", name: "Metro Ambulance", npi: "1866666666", type: "professional", taxonomy: "3416L0300X", specialty: "Ground ambulance", networkIds: [] },
        { id: "prv-mercy", name: "Mercy General Hospital", npi: "1877777777", type: "institutional", taxonomy: "282N00000X", specialty: "General acute care hospital", networkIds: ["net-centivo", "net-cigna"] },
        { id: "prv-lakeside", name: "Lakeside Surgery Center", npi: "1888888888", type: "institutional", taxonomy: "261QA1903X", specialty: "Ambulatory surgery center", networkIds: [] },
    ],
    families: [
        { id: "fam-doe", familyNumber: "F-1001", name: "Doe family", planId: "PP01" },
        { id: "fam-kim", familyNumber: "F-1002", name: "Kim family", planId: "PP01" },
        { id: "fam-lee", familyNumber: "F-2001", name: "Lee family", planId: "PPO01" },
        { id: "fam-green", familyNumber: "F-2002", name: "Green family", planId: "PPO01" },
        { id: "fam-diaz", familyNumber: "F-3001", name: "Diaz family", planId: "EPO01" },
        { id: "fam-shah", familyNumber: "F-4001", name: "Shah family", planId: "TRAD01" },
    ],
    members: [
        { id: "mem-jane", memberNumber: "M-1001", name: "Jane Doe", familyId: "fam-doe", relationship: "subscriber", dateOfBirth: "1988-04-12", sex: "F", pcpProviderId: "prv-patel", planId: "PP01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 120, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-john", memberNumber: "M-1001-02", name: "John Doe", familyId: "fam-doe", relationship: "spouse", dateOfBirth: "1986-09-03", sex: "M", pcpProviderId: "prv-patel", planId: "PP01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 300, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-lily", memberNumber: "M-1001-03", name: "Lily Doe", familyId: "fam-doe", relationship: "child", dateOfBirth: "2018-06-21", sex: "F", pcpProviderId: "prv-okafor", planId: "PP01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 0, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-max", memberNumber: "M-1001-04", name: "Max Doe", familyId: "fam-doe", relationship: "child", dateOfBirth: "1999-08-20", sex: "M", pcpProviderId: "prv-patel", planId: "PP01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 0, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-alex", memberNumber: "M-1002", name: "Alex Kim", familyId: "fam-kim", relationship: "subscriber", dateOfBirth: "1992-01-30", sex: "M", pcpProviderId: "prv-patel", planId: "PP01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 0, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-sam", memberNumber: "M-2001", name: "Sam Lee", familyId: "fam-lee", relationship: "subscriber", dateOfBirth: "1984-11-05", sex: "M", pcpProviderId: "prv-wright", planId: "PPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 400, outOfPocketMet: 650, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: { outpatientRehabilitation: 18, chiropractice: 11 } },
        { id: "mem-mia", memberNumber: "M-2001-02", name: "Mia Lee", familyId: "fam-lee", relationship: "spouse", dateOfBirth: "1985-02-17", sex: "F", pcpProviderId: "prv-wright", planId: "PPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 1500, outOfPocketMet: 2100, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-noah", memberNumber: "M-2001-03", name: "Noah Lee", familyId: "fam-lee", relationship: "child", dateOfBirth: "2015-10-09", sex: "M", pcpProviderId: "prv-okafor", planId: "PPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 900, outOfPocketMet: 1200, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-pat", memberNumber: "M-2002", name: "Pat Green", familyId: "fam-green", relationship: "subscriber", dateOfBirth: "1979-07-22", sex: "U", pcpProviderId: "prv-wright", planId: "PPO01", coverageFrom: "2026-01-01", coverageTo: "2026-03-31", deductibleMet: 0, outOfPocketMet: 0, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-omar", memberNumber: "M-3001", name: "Omar Diaz", familyId: "fam-diaz", relationship: "subscriber", dateOfBirth: "1990-03-14", sex: "M", pcpProviderId: "prv-wright", planId: "EPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 200, outOfPocketMet: 600, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-lena", memberNumber: "M-3001-02", name: "Lena Diaz", familyId: "fam-diaz", relationship: "domesticPartner", dateOfBirth: "1991-12-01", sex: "F", pcpProviderId: "prv-wright", planId: "EPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 500, outOfPocketMet: 2900, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-sofia", memberNumber: "M-3001-03", name: "Sofia Diaz", familyId: "fam-diaz", relationship: "child", dateOfBirth: "2021-05-28", sex: "F", pcpProviderId: "prv-okafor", planId: "EPO01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 300, outOfPocketMet: 2400, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-ria", memberNumber: "M-4001", name: "Ria Shah", familyId: "fam-shah", relationship: "subscriber", dateOfBirth: "1975-09-09", sex: "F", pcpProviderId: "prv-wright", planId: "TRAD01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 1800, outOfPocketMet: 2400, oonDeductibleMet: 4500, oonOutOfPocketMet: 0, unitsUsed: {} },
        { id: "mem-dev", memberNumber: "M-4001-02", name: "Dev Shah", familyId: "fam-shah", relationship: "spouse", dateOfBirth: "1973-06-30", sex: "M", pcpProviderId: "", planId: "TRAD01", coverageFrom: "2026-01-01", coverageTo: "2026-12-31", deductibleMet: 0, outOfPocketMet: 0, oonDeductibleMet: 0, oonOutOfPocketMet: 0, unitsUsed: {} },
    ],
    feeSchedules: [
        { id: "fs-centivo", networkId: "net-centivo", providerId: "", name: "Centivo 2026", fallbackPercentOfBilled: 50, excludedCodes: [], lines: rates(1, BASE_RATES) },
        { id: "fs-cigna", networkId: "net-cigna", providerId: "", name: "Cigna 2026", fallbackPercentOfBilled: 55, excludedCodes: ["97811"], lines: rates(1.2, BASE_RATES) },
        { id: "fs-oon", networkId: "net-oon", providerId: "", name: "Out-of-network 2026", fallbackPercentOfBilled: 40, excludedCodes: [], lines: rates(1.4, BASE_RATES) },
        {
            id: "fs-mercy-cigna",
            networkId: "net-cigna",
            providerId: "prv-mercy",
            name: "Mercy General on Cigna",
            fallbackPercentOfBilled: 55,
            excludedCodes: [],
            lines: [{ code: "0120", amount: 2300 }, { code: "0450", amount: 1250 }],
        },
    ],
    umRules: [
        { id: "um-cigna-imaging", name: "MRI and CT need approval", source: "contract", networkId: "net-cigna", serviceKey: "advanceDiagnostic", when: "always", missingAuth: "penalty", penalty: { valueType: "percent", value: 50 } },
        { id: "um-cigna-inpatient", name: "Hospital stays need approval", source: "contract", networkId: "net-cigna", serviceKey: "inpatientHospital", when: "always", missingAuth: "penalty", penalty: { valueType: "amount", value: 1000 } },
        { id: "um-ambulance", name: "Non-emergency ambulance rides need approval", source: "plan", networkId: "", serviceKey: "groundAmbulance", when: "notEmergency", missingAuth: "deny", penalty: null },
        { id: "um-surgery", name: "Outpatient surgery needs approval", source: "plan", networkId: "", serviceKey: "outpatientSurgery", when: "always", missingAuth: "pend", penalty: null },
        { id: "um-rehab", name: "Therapy needs approval", source: "plan", networkId: "", serviceKey: "outpatientRehabilitation", when: "always", missingAuth: "penalty", penalty: { valueType: "amount", value: 50 } },
    ],
    medicalNecessity: [
        {
            id: "mn-lumbar-mri",
            name: "Lumbar spine MRI",
            serviceKey: "advanceDiagnostic",
            codes: ["72148", "72149", "72158"],
            diagnoses: ["M54.16", "M51.26", "M54.50", "C50.911"],
            criteria: [
                "Back pain for 6 weeks or more, and therapy or medicine didn't help",
                "A red flag: new weakness or numbness, bladder or bowel changes, cancer history, or fever",
                "Surgery or a spine injection is being planned",
            ],
            mode: "any",
            maxUnits: 1,
            validDays: 60,
            notMet: "deny",
        },
        {
            id: "mn-head-ct",
            name: "Head CT or brain MRI",
            serviceKey: "advanceDiagnostic",
            codes: ["70450", "70551", "70553"],
            diagnoses: ["S06.0X1A", "R29.818", "G43.909", "R51.9"],
            criteria: [
                "The worst headache of their life, starting suddenly",
                "A head injury with loss of consciousness",
                "New nerve or brain symptoms, such as weakness or trouble speaking",
            ],
            mode: "any",
            maxUnits: 1,
            validDays: 30,
            notMet: "deny",
        },
        {
            id: "mn-knee-scope",
            name: "Knee arthroscopy for a torn meniscus",
            serviceKey: "outpatientSurgery",
            codes: ["29880", "29881"],
            diagnoses: ["S83.241A", "M23.221"],
            criteria: [
                "The knee locks, catches, or gives way",
                "An MRI shows a meniscus tear",
                "4 weeks of therapy or medicine didn't help",
            ],
            mode: "all",
            maxUnits: 1,
            validDays: 90,
            notMet: "pend",
        },
        {
            id: "mn-inpatient-heart",
            name: "Hospital stay for chest pain or pneumonia",
            serviceKey: "inpatientHospital",
            codes: [],
            diagnoses: ["I20.9", "I21.4", "R07.9", "J18.9"],
            criteria: [
                "Needs IV medicine or heart monitoring that can't be done at home",
                "Unstable vital signs or an abnormal heart test",
                "Low oxygen that needs treatment",
            ],
            mode: "any",
            maxUnits: 3,
            validDays: 30,
            notMet: "pend",
        },
        {
            id: "mn-therapy",
            name: "Outpatient physical therapy",
            serviceKey: "outpatientRehabilitation",
            codes: [],
            diagnoses: ["M54.50", "M54.16", "M62.81", "Z47.1", "S13.4XXA", "M17.11", "S83.241A"],
            criteria: [
                "A therapist's evaluation sets goals",
                "The member is getting better with therapy",
            ],
            mode: "all",
            maxUnits: 12,
            validDays: 90,
            notMet: "pend",
        },
        {
            id: "mn-ambulance",
            name: "Non-emergency ambulance ride",
            serviceKey: "groundAmbulance",
            codes: [],
            diagnoses: [],
            criteria: [
                "The member is bed-confined",
                "The member needs oxygen or monitoring during the ride",
            ],
            mode: "any",
            maxUnits: 2,
            validDays: 30,
            notMet: "deny",
        },
    ],
    authorizations: [
        { id: "auth-1001", number: "REF-1001", type: "referral", status: "approved", memberId: "mem-jane", serviceKey: "specialistVisit", providerId: "prv-chen", from: "2026-01-01", to: "2026-06-30", units: 6, used: 1, agreedAmount: null, diagnosisCodes: ["R07.9"], criteriaId: "", decision: "Referral from the member's PCP.", notes: "PCP referral to cardiology." },
        { id: "auth-1002", number: "AUTH-1002", type: "authorization", status: "approved", memberId: "mem-ria", serviceKey: "advanceDiagnostic", providerId: "", from: "2026-03-01", to: "2026-12-31", units: 1, used: 0, agreedAmount: null, diagnosisCodes: ["M54.16"], criteriaId: "mn-lumbar-mri", decision: "Meets Lumbar spine MRI: a red flag is present.", notes: "Lumbar MRI, any in-network imaging center." },
        { id: "auth-1003", number: "AUTH-1003", type: "authorization", status: "approved", memberId: "mem-ria", serviceKey: "inpatientHospital", providerId: "prv-mercy", from: "2026-05-01", to: "2026-05-31", units: 2, used: 0, agreedAmount: null, diagnosisCodes: ["I20.9"], criteriaId: "mn-inpatient-heart", decision: "Meets the hospital stay criteria for 2 days. More days need review.", notes: "Two approved days. A third day needs more review." },
        { id: "auth-1004", number: "AUTH-1004", type: "authorization", status: "approved", memberId: "mem-ria", serviceKey: "outpatientSurgery", providerId: "prv-lakeside", from: "2026-04-01", to: "2026-06-30", units: 1, used: 0, agreedAmount: 4000, diagnosisCodes: ["S83.241A"], criteriaId: "mn-knee-scope", decision: "Meets Knee arthroscopy for a torn meniscus: all criteria met.", notes: "Single case agreement: the surgery center takes $4,000 for the knee scope." },
        { id: "auth-1005", number: "AUTH-1005", type: "authorization", status: "approved", memberId: "mem-sam", serviceKey: "outpatientRehabilitation", providerId: "prv-peak", from: "2026-01-01", to: "2026-12-31", units: 24, used: 20, agreedAmount: null, diagnosisCodes: ["M54.50"], criteriaId: "mn-therapy", decision: "Meets Outpatient physical therapy.", notes: "Physical therapy after back strain." },
        { id: "auth-1006", number: "AUTH-1006", type: "authorization", status: "denied", memberId: "mem-jane", serviceKey: "advanceDiagnostic", providerId: "", from: "2026-01-01", to: "2026-12-31", units: 1, used: 0, agreedAmount: null, diagnosisCodes: ["M54.50"], criteriaId: "mn-lumbar-mri", decision: "Doesn't meet Lumbar spine MRI: no red flag, and less than 6 weeks of therapy.", notes: "Denied: try physical therapy first." },
        { id: "auth-1007", number: "AUTH-1007", type: "authorization", status: "approved", memberId: "mem-noah", serviceKey: "outpatientRehabilitation", providerId: "prv-peak", from: "2026-04-01", to: "2026-09-30", units: 12, used: 2, agreedAmount: null, diagnosisCodes: ["S83.241A"], criteriaId: "mn-therapy", decision: "Meets Outpatient physical therapy.", notes: "Therapy after a knee injury at soccer." },
    ],
});

export const newId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

export function loadEmr() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (raw) return JSON.parse(raw);
    } catch {
        // A bad session value falls through to the seed data.
    }
    const data = seed();
    sessionStorage.setItem(KEY, JSON.stringify(data));
    return data;
}

export function saveEmr(data) {
    sessionStorage.setItem(KEY, JSON.stringify(data));
}

export function resetEmr() {
    const data = seed();
    saveEmr(data);
    return data;
}
